import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LiveQA, Brainstorming, LivePolls } from '../components/InteractionPillars';
import ActiveParticipants from '../components/ActiveParticipants';
import AchievementToast from '../components/AchievementToast';
import Footer from '../components/Footer';
import Breadcrumbs from '../components/Breadcrumbs';
import { Radio, MessageSquare, BarChart3, BrainCircuit, Share2, Info, Lock, Pause, Archive, Shield, User, Mic2, Clock, ArrowLeft, ArrowRight, TrendingUp, Sparkles } from 'lucide-react';
import { cn, formatTime, getDurationInSeconds, normalizeSpeakers } from '../utils';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { SESSION_ROLES } from '../utils/permissions';
import { useRoom } from '../hooks/useRoom';
import { useRoomTheme } from '../hooks/useRoomTheme';

const Room = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { addToast } = useToast();
  
  const {
    roomData,
    sessionState,
    sessionRole,
    setSessionRole,
    timeLeft,
    reactions,
    isUnlocked,
    isRestricted,
    showPasswordPrompt,
    accessError,
    isInitialLoading,
    isModerator,
    handlePasswordSubmit,
    updateSessionStatus,
    handleReaction,
    handleInvite
  } = useRoom(id);

  const { theme } = useRoomTheme(roomData?.type);

  const [activeTab, setActiveTab] = useState('qa');
  const [passwordInput, setPasswordInput] = useState('');
  const [moderationLogs, setModerationLogs] = useState([]);
  const [latestAchievement, setLatestAchievement] = useState(null);
  const [lastNotificationId, setLastNotificationId] = useState(null);

  useEffect(() => {
    if (!user) return;

    const checkAchievements = async () => {
      try {
        const data = await api.get('/notifications?unread=1&type=badge');
        if (Array.isArray(data) && data.length > 0) {
          const newest = data[0];
          if (newest.id !== lastNotificationId) {
            setLatestAchievement({
              name: newest.title.replace('New Achievement!', '').trim() || 'Level Up!',
              description: newest.description
            });
            setLastNotificationId(newest.id);
            api.post(`/notifications/${newest.id}/read`);
          }
        }
      } catch (err) {
        console.error('Failed to check achievements', err);
      }
    };

    checkAchievements();
    const intervalId = setInterval(checkAchievements, 15000);
    return () => clearInterval(intervalId);
  }, [user, lastNotificationId]);

  const fetchLogs = useCallback(async () => {
    if (!roomData?.id) return;
    try {
      const data = await api.get(`/admin/logs?room_id=${roomData.id}`);
      const validData = Array.isArray(data) ? data : [];
      setModerationLogs(validData.map(log => ({
        time: new Date(log.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        admin: log.user?.name || 'Moderator',
        action: log.action,
        target: log.target_preview || log.target_type
      })));
    } catch (err) {
      console.error('Failed to fetch moderation logs', err);
    }
  }, [roomData?.id]);

  useEffect(() => {
    if (isModerator) {
      fetchLogs();
      const intervalId = setInterval(fetchLogs, 10000);
      return () => clearInterval(intervalId);
    }
  }, [isModerator, fetchLogs]);

  const onPasswordSubmit = (e) => {
    e.preventDefault();
    handlePasswordSubmit(passwordInput);
  };

  if (isInitialLoading || !roomData) {
    return (
      <div className="min-h-screen bg-brand-light flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 font-medium mb-4">Loading session...</p>
          <button onClick={() => navigate(-1)} className="text-brand-indigo font-bold text-sm">Go Back</button>
        </div>
      </div>
    );
  }

  const expectedDurationSeconds = getDurationInSeconds(roomData?.settings?.duration);
  const sessionSpeakers = normalizeSpeakers(roomData?.speakers || roomData?.settings?.speakers);
  const tabs = ['qa', 'brainstorm', 'poll'];
  
  const handleDragEnd = (event, info) => {
    const swipeThreshold = 50;
    if (info.offset.x > swipeThreshold) {
      const currentIndex = tabs.indexOf(activeTab);
      if (currentIndex > 0) setActiveTab(tabs[currentIndex - 1]);
    } else if (info.offset.x < -swipeThreshold) {
      const currentIndex = tabs.indexOf(activeTab);
      if (currentIndex < tabs.length - 1) setActiveTab(tabs[currentIndex + 1]);
    }
  };

  const renderTabContent = (tab) => {
    switch(tab) {
      case 'qa':
        return (
          <LiveQA 
            sessionState={sessionState} 
            isModerator={isModerator} 
            sessionId={roomData?.id}
            communityId={roomData?.community_id}
            typingCount={roomData.typing_count || 0}
            roomData={roomData}
          />
        );
      case 'brainstorm':
        return (
          <Brainstorming 
            sessionState={sessionState} 
            isModerator={isModerator} 
            sessionId={roomData?.id}
            communityId={roomData?.community_id}
            typingCount={roomData.typing_count || 0}
            roomData={roomData}
          />
        );
      case 'poll':
        return (
          <LivePolls 
            sessionState={sessionState} 
            isModerator={isModerator} 
            sessionId={roomData?.id}
            communityId={roomData?.community_id}
            roomData={roomData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-brand-light">
      <AnimatePresence>
        {isRestricted && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-md w-full bg-white rounded-3xl border border-gray-100 p-8 shadow-2xl text-center"
            >
              <div className="w-16 h-16 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Shield size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Restricted Access</h2>
              <p className="text-gray-500 font-medium mb-8 leading-relaxed">
                This session is invite-only and restricted to specific participants. 
                If you believe this is an error, please contact the host.
              </p>
              
              <button 
                onClick={() => navigate(-1)}
                className="w-full bg-brand-indigo text-white font-bold py-4 rounded-2xl shadow-lg shadow-brand-indigo/20 hover:bg-brand-indigo/90 transition-all active:scale-95 flex items-center justify-center gap-2"
              >
                <ArrowLeft size={20} />
                Return to Safety
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPasswordPrompt && !isUnlocked && !isRestricted && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-md w-full bg-white rounded-3xl border border-gray-100 p-8 shadow-2xl text-center"
            >
              <div className="w-16 h-16 bg-brand-indigo/10 text-brand-indigo rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Lock size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Private Session</h2>
              <p className="text-gray-500 font-medium mb-8">This session is password protected. Please enter the access code to join.</p>
              
              <form onSubmit={onPasswordSubmit} className="space-y-4">
                <div>
                  <input 
                    type="password"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    placeholder="Enter password"
                    className={cn(
                      "w-full px-6 py-4 bg-gray-50 border-2 rounded-2xl focus:ring-4 focus:ring-brand-indigo/5 font-bold transition-all text-center text-xl tracking-widest outline-none",
                      accessError ? "border-red-100 bg-red-50 text-red-600 focus:ring-red-100" : "border-gray-50 focus:border-brand-indigo/20"
                    )}
                    autoFocus
                  />
                  {accessError && (
                    <p className="text-red-500 text-xs font-bold mt-3 animate-in fade-in slide-in-from-top-1">{accessError}</p>
                  )}
                </div>
                <button 
                  type="submit"
                  className="w-full bg-brand-indigo text-white font-bold py-4 rounded-2xl shadow-lg shadow-brand-indigo/20 hover:bg-brand-indigo/90 transition-all active:scale-95 flex items-center justify-center gap-2"
                >
                  Unlock Session
                  <ArrowRight size={20} />
                </button>
                <button 
                  type="button"
                  onClick={() => navigate(-1)}
                  className="w-full text-gray-400 font-bold py-2 text-sm hover:text-gray-600 transition-colors"
                >
                  Go Back
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {latestAchievement && (
          <AchievementToast 
            achievement={latestAchievement} 
            onClose={() => setLatestAchievement(null)} 
          />
        )}
      </AnimatePresence>

      {/* Floating Controls */}
      <div className="fixed bottom-24 right-4 md:right-8 z-40 flex flex-col gap-3 md:gap-4 items-center">
        {/* Reaction Bar - Visible to everyone */}
        <div className="bg-white border border-gray-100 rounded-3xl p-1.5 md:p-2 shadow-2xl flex flex-col gap-1.5 md:gap-2">
          {['🔥', '❤️', '👏', '🧠', '😮'].map((emoji) => (
            <button 
              key={emoji}
              onClick={() => handleReaction(emoji)}
              disabled={sessionState !== 'live'}
              className="p-2.5 md:p-3 text-lg md:text-xl hover:bg-gray-50 rounded-2xl transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {emoji}
            </button>
          ))}
        </div>
        <p className="text-[8px] md:text-xxs font-bold text-center text-gray-400 uppercase tracking-[0.15em] mb-2">React</p>

        {/* Moderator/Admin Controls */}
        {isModerator && (
          <>
            {/* Role Switcher */}
            <div className="bg-white border border-gray-100 rounded-3xl p-1.5 md:p-2 shadow-2xl flex flex-col gap-1.5 md:gap-2">
              <button 
                onClick={() => setSessionRole(SESSION_ROLES.HOST)}
                className={cn("p-2.5 md:p-3 rounded-2xl transition-all active:scale-95", sessionRole === SESSION_ROLES.HOST ? "bg-brand-indigo text-white" : "text-gray-400 hover:bg-gray-50")}
                title="Host Role"
              >
                <Shield size={18} />
              </button>
              <button 
                onClick={() => setSessionRole(SESSION_ROLES.SPEAKER)}
                className={cn("p-2.5 md:p-3 rounded-2xl transition-all active:scale-95", sessionRole === SESSION_ROLES.SPEAKER ? "bg-brand-indigo text-white" : "text-gray-400 hover:bg-gray-50")}
                title="Speaker Role"
              >
                <Mic2 size={18} />
              </button>
              <button 
                onClick={() => setSessionRole(SESSION_ROLES.PARTICIPANT)}
                className={cn("p-2.5 md:p-3 rounded-2xl transition-all active:scale-95", sessionRole === SESSION_ROLES.PARTICIPANT ? "bg-brand-indigo text-white" : "text-gray-400 hover:bg-gray-50")}
                title="Participant Role"
              >
                <User size={18} />
              </button>
            </div>
            <p className="text-[8px] md:text-xxs font-bold text-center text-gray-400 uppercase tracking-[0.15em] mb-2">Role</p>

            <div className="bg-white border border-gray-100 rounded-3xl p-1.5 md:p-2 shadow-2xl flex flex-col gap-1.5 md:gap-2">
              <button 
                onClick={() => updateSessionStatus('live')}
                className={cn("p-2.5 md:p-3 rounded-2xl transition-all active:scale-95", sessionState === 'live' ? "bg-red-500 text-white" : "text-gray-400 hover:bg-gray-50")}
                title="Go Live"
              >
                <Radio size={18} />
              </button>
              <button 
                onClick={() => updateSessionStatus('paused')}
                className={cn("p-2.5 md:p-3 rounded-2xl transition-all active:scale-95", sessionState === 'paused' ? "bg-orange-500 text-white" : "text-gray-400 hover:bg-gray-50")}
                title="Pause Participation"
              >
                <Pause size={18} />
              </button>
              <button 
                onClick={() => updateSessionStatus('locked')}
                className={cn("p-2.5 md:p-3 rounded-2xl transition-all active:scale-95", sessionState === 'locked' ? "bg-gray-800 text-white" : "text-gray-400 hover:bg-gray-50")}
                title="Lock Room (Read-only)"
              >
                <Lock size={18} />
              </button>
              <button 
                onClick={() => updateSessionStatus('archived')}
                className={cn("p-2.5 md:p-3 rounded-2xl transition-all active:scale-95", sessionState === 'archived' ? "bg-brand-indigo text-white" : "text-gray-400 hover:bg-gray-50")}
                title="Archive Session"
              >
                <Archive size={18} />
              </button>
            </div>
            <p className="text-[8px] md:text-xxs font-bold text-center text-gray-400 uppercase tracking-[0.15em]">Admin</p>
          </>
        )}
      </div>

      {/* Floating Reactions Canvas */}
      <div className="fixed inset-0 pointer-events-none z-50">
        <AnimatePresence>
          {Array.isArray(reactions) && reactions.map((r) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: '80vh', x: `${r.x}vw` }}
              animate={{ opacity: 1, y: '20vh' }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2, ease: "easeOut" }}
              className="absolute text-3xl"
            >
              {r.emoji}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Room Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-30">
        {sessionState === 'archived' && (
          <div className="bg-brand-indigo text-white px-4 py-2 text-center text-xxs font-bold uppercase tracking-[0.15em] animate-in fade-in slide-in-from-top duration-500">
            This session is archived. Knowledge is preserved for the community.
          </div>
        )}
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-2 md:py-4 flex items-center justify-between gap-3 md:gap-4">
          <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1">
            <div className="hidden md:block mr-2">
              <Breadcrumbs 
                items={[
                  roomData.community_name ? { label: roomData.community_name, path: `/c/${roomData.community_id}`, maxWidth: '150px' } : { label: 'Explore', path: '/explore' },
                  { label: roomData.title, maxWidth: '200px' }
                ]} 
              />
            </div>
            <button 
              onClick={() => navigate(-1)}
              className="p-1.5 md:p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-600 group shrink-0"
              aria-label="Go back"
            >
              <ArrowLeft size={18} className="md:w-5 md:h-5 group-hover:-translate-x-0.5 transition-transform" />
            </button>

            <div className={cn(
              "w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl flex items-center justify-center text-white shadow-lg transition-all shrink-0",
              sessionState === 'live' ? "bg-red-500 shadow-red-100" : 
              sessionState === 'paused' ? "bg-orange-500 shadow-orange-100" :
              sessionState === 'locked' ? "bg-gray-800 shadow-gray-100" : "bg-brand-indigo shadow-brand-indigo/20"
            )}>
              {sessionState === 'live' && <Radio size={20} className="md:w-6 md:h-6 animate-pulse" />}
              {sessionState === 'paused' && <Pause size={20} className="md:w-6 md:h-6" />}
              {sessionState === 'locked' && <Lock size={20} className="md:w-6 md:h-6" />}
              {sessionState === 'archived' && <Archive size={20} className="md:w-6 md:h-6" />}
            </div>
            
            <div className="flex flex-col items-start min-w-0 flex-1 px-1 md:px-2">
              <div className="flex items-center gap-1.5 md:gap-2 mb-0.5 min-w-0 w-full">
                <span className={cn(
                  "text-[7px] md:text-xxs font-bold px-1 md:px-2 py-0.5 rounded uppercase tracking-[0.15em] border flex items-center gap-1 shrink-0",
                  sessionState === 'live' ? "text-red-500 bg-red-50 border-red-100" : 
                  sessionState === 'paused' ? "text-orange-500 bg-orange-50 border-orange-100" :
                  sessionState === 'locked' ? "text-gray-600 bg-gray-50 border-gray-200" : "text-brand-indigo bg-brand-indigo/10 border-brand-indigo/10"
                )}>
                  {sessionState === 'live' && <span className="w-1 h-1 bg-red-500 rounded-full animate-pulse"></span>}
                  {sessionState}
                </span>
                <h1 className="text-sm md:text-xl font-bold text-gray-900 tracking-tight truncate flex-1 text-left">{roomData.title}</h1>
              </div>
              <div className="flex items-center gap-1.5 md:gap-2">
                <div className="flex items-center gap-1 overflow-hidden max-w-[120px] md:max-w-none">
                  <Shield size={10} className="text-brand-indigo shrink-0" />
                  <span className="text-[9px] md:text-xxs font-bold text-gray-900 truncate">{roomData.host || 'Host'}</span>
                </div>
                <span className="text-gray-300 shrink-0">•</span>
                <div className={cn(
                  "flex items-center gap-1 px-1 md:px-1.5 py-0.5 rounded-xl border font-bold text-[8px] md:text-xxs shrink-0",
                  sessionState === 'upcoming' ? "bg-amber-50 text-amber-600 border-amber-100" : 
                  (expectedDurationSeconds > 0 && timeLeft > expectedDurationSeconds) ? "bg-orange-50 text-orange-600 border-orange-100" : "bg-red-50 text-red-600 border-red-100"
                )}>
                  <Clock size={10} className={cn("md:w-3 md:h-3", timeLeft < 300 && sessionState === 'live' && "animate-pulse")} />
                  <span>{sessionState === 'upcoming' ? `Starts in ${formatTime(timeLeft)}` : formatTime(timeLeft)}</span>
                  {expectedDurationSeconds > 0 && sessionState === 'live' && (
                    <span className="opacity-60 ml-1">/ {roomData.settings.duration}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4 shrink-0">
            <ActiveParticipants 
              participants={roomData.participants || []} 
              count={roomData.members_count || 0}
              variant={theme.color === 'red' ? 'red' : theme.color === 'orange' ? 'orange' : 'indigo'}
            />
            <button 
              onClick={handleInvite}
              className="flex items-center justify-center gap-2 bg-gray-50 text-gray-600 px-3 md:px-4 py-2 rounded-xl font-bold text-xs md:text-sm hover:bg-gray-100 transition-all active:scale-95 border border-gray-100"
            >
              <Share2 size={16} className="md:w-[18px] md:h-[18px]" />
              <span className="hidden xs:inline">Share</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-6">
            {/* Desktop Tabs */}
            <div className="hidden lg:flex items-center gap-2 p-1.5 bg-white rounded-3xl border border-gray-100 w-fit">
              <button 
                onClick={() => setActiveTab('qa')}
                className={cn(
                  "px-6 py-2 rounded-xl font-bold text-sm transition-all active:scale-95 flex items-center gap-2",
                  activeTab === 'qa' ? "bg-brand-indigo text-white shadow-lg shadow-brand-indigo/20" : "text-gray-400 hover:bg-gray-50"
                )}
              >
                <MessageSquare size={18} />
                Live Q&A
              </button>
              <button 
                onClick={() => setActiveTab('brainstorm')}
                className={cn(
                  "px-6 py-2 rounded-xl font-bold text-sm transition-all active:scale-95 flex items-center gap-2",
                  activeTab === 'brainstorm' ? "bg-brand-indigo text-white shadow-lg shadow-brand-indigo/20" : "text-gray-400 hover:bg-gray-50"
                )}
              >
                <BrainCircuit size={18} />
                Brainstorming
              </button>
              <button 
                onClick={() => setActiveTab('poll')}
                className={cn(
                  "px-6 py-2 rounded-xl font-bold text-sm transition-all active:scale-95 flex items-center gap-2",
                  activeTab === 'poll' ? "bg-brand-indigo text-white shadow-lg shadow-brand-indigo/20" : "text-gray-400 hover:bg-gray-50"
                )}
              >
                <BarChart3 size={18} />
                Live Polls
              </button>
            </div>

            {/* Mobile Tab Switcher */}
            <div className="lg:hidden flex bg-white p-1 rounded-3xl border border-gray-100 overflow-x-auto no-scrollbar">
              <button 
                onClick={() => setActiveTab('qa')}
                className={cn(
                  "flex-1 min-w-[80px] flex items-center justify-center gap-1.5 py-2.5 rounded-xl font-bold text-xs xs:text-sm transition-all active:scale-95 whitespace-nowrap",
                  activeTab === 'qa' ? "bg-brand-indigo text-white shadow-lg shadow-brand-indigo/20" : "text-gray-400"
                )}
              >
                <MessageSquare size={16} />
                Q&A
              </button>
              <button 
                onClick={() => setActiveTab('brainstorm')}
                className={cn(
                  "flex-1 min-w-[80px] flex items-center justify-center gap-1.5 py-2.5 rounded-xl font-bold text-xs xs:text-sm transition-all active:scale-95 whitespace-nowrap",
                  activeTab === 'brainstorm' ? "bg-brand-indigo text-white shadow-lg shadow-brand-indigo/20" : "text-gray-400"
                )}
              >
                <BrainCircuit size={16} />
                Ideation
              </button>
              <button 
                onClick={() => setActiveTab('poll')}
                className={cn(
                  "flex-1 min-w-[80px] flex items-center justify-center gap-1.5 py-2.5 rounded-xl font-bold text-xs xs:text-sm transition-all active:scale-95 whitespace-nowrap",
                  activeTab === 'poll' ? "bg-brand-indigo text-white shadow-lg shadow-brand-indigo/20" : "text-gray-400"
                )}
              >
                <BarChart3 size={16} />
                Polls
              </button>
            </div>

            <div className="relative min-h-[500px] h-[calc(100vh-320px)] lg:h-[calc(100vh-280px)] overflow-hidden bg-white rounded-3xl border border-gray-100 shadow-xl shadow-brand-indigo/5">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="h-full w-full"
                >
                  {renderTabContent(activeTab)}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          <div className="lg:col-span-4 space-y-6">
            {sessionState === 'archived' && (
              <div className="bg-gradient-to-br from-brand-indigo to-indigo-700 rounded-3xl p-6 text-white shadow-xl shadow-brand-indigo/20">
                <h3 className="font-bold flex items-center gap-2 mb-4 uppercase tracking-[0.15em] text-xs">
                  <TrendingUp size={18} />
                  Session Summary
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-4">
                  <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20">
                    <p className="text-xxs font-bold uppercase tracking-[0.15em] text-brand-indigo/60 mb-1">Participants</p>
                    <p className="text-sm font-bold leading-tight">{(roomData.participants || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20">
                    <p className="text-xxs font-bold uppercase tracking-[0.15em] text-brand-indigo/60 mb-1">Questions</p>
                    <p className="text-sm font-bold leading-tight">{roomData.questions_count || 0}</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20">
                    <p className="text-xxs font-bold uppercase tracking-[0.15em] text-brand-indigo/60 mb-1">Polls</p>
                    <p className="text-sm font-bold leading-tight">{roomData.polls_count || 0}</p>
                  </div>
                </div>
              </div>
            )}
            <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm">
              <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-4">
                <Info size={18} className="text-brand-indigo" />
                About this Session
              </h3>
              <p className="text-gray-500 font-medium text-sm leading-relaxed mb-6">
                {roomData.description || roomData.desc || (roomData.type ? `Hosted by ${roomData.host || roomData.owner?.name || 'Host'} for ${roomData.community?.name || 'this community'} in a ${roomData.type} session.` : 'No description provided for this session.')}
              </p>

              {/* Guest Speakers */}
              {(roomData.speakers || (roomData.settings && roomData.settings.speakers)) && (
                <div className="mb-6 pt-4 border-t border-gray-50">
                   <h4 className="text-xxs font-bold text-gray-400 uppercase tracking-[0.15em] mb-3 flex items-center gap-2">
                      <Mic2 size={12} className={theme.text} />
                      Guest Speaker(s)
                   </h4>
                   <div className="flex flex-wrap gap-2">
                      {sessionSpeakers.map((speaker, index) => (
                        <div key={index} className={cn("px-3 py-1.5 rounded-xl flex items-center gap-2 border transition-all", theme.lightBg, theme.lightBorder)}>
                           <div className={cn("w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white shadow-sm", theme.bg)}>
                              {speaker?.charAt(0)?.toUpperCase()}
                           </div>
                           <span className={cn("text-xs font-bold", theme.text)}>{speaker}</span>
                        </div>
                      ))}
                   </div>
                </div>
              )}
              
              {isModerator && (
                 <div className="mt-8 pt-8 border-t border-gray-50">
                    <h4 className="text-xxs font-bold text-gray-400 uppercase tracking-[0.15em] mb-4">Moderator Logs</h4>
                    <div className="space-y-3">
                       {moderationLogs.length > 0 ? moderationLogs.slice(0, 5).map((log, i) => (
                         <div key={i} className="flex gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-brand-indigo mt-1.5 shrink-0"></div>
                            <div>
                               <p className="text-[10px] font-bold text-gray-900 leading-tight">{log.admin} {log.action} {log.target}</p>
                               <p className="text-[9px] text-gray-400 font-medium uppercase tracking-[0.15em] mt-0.5">{log.time}</p>
                            </div>
                         </div>
                       )) : (
                         <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.15em] italic">No recent activity logged.</p>
                       )}
                    </div>
                 </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Room;
